real.realtime = (function(real) {

    let isReady = false;
    let activeConnection = null;
    let readyFnStack = [];
    const connectedFnStack = [];

    let connectTimer = null;
    let connectAttempts = 0;
    let connectLastAttempt = null;

    const delayBetweenConnectAttempts = 5*1000;

    // This wrapper still has a transport abstraction from the websocket migration,
    // but uwebsocket is the only backend in practice. Keep this layer focused on
    // desired subscriptions; transport state and reconnect mechanics belong below.
    const topicsStack = [];
    // Optional subscribe callbacks are for UI that must wait for server ACK before
    // continuing, such as AG-UI chat rendering. The public callback form supports a
    // single topic because there is only one logical ACK/error result to report.
    const subscribeCallbackStack = {};

    const addTopicToStack = function(topic) {
        if (topicsStack.indexOf(topic) !== -1) {
            return false;
        }

        topicsStack.push(topic);
        return true;
    };

    const removeTopicFromStack = function(topic) {
        const index = topicsStack.indexOf(topic);
        if (index >= 0) {
            topicsStack.splice(index, 1);
        }
    };

    const addSubscribeCallback = function(topic, token, callback, timeoutMs) {
        if (!subscribeCallbackStack[topic]) {
            subscribeCallbackStack[topic] = {
                callbacks: [],
                token,
                timeoutMs,
            };
        }

        subscribeCallbackStack[topic].callbacks.push(callback);
    };

    const settleSubscribeCallbacks = function(topic, error = null) {
        const pending = subscribeCallbackStack[topic];
        if (!pending) {
            return;
        }

        delete subscribeCallbackStack[topic];
        pending.callbacks.forEach(callback => callback(error));
    };

    const handleSubscriptionResult = function(topic, error = null, retryAfterReconnect = false) {
        const pending = subscribeCallbackStack[topic];
        if (!pending) {
            return;
        }

        if (retryAfterReconnect) {
            // Inline topic tokens are scoped to one websocket id. Keep the pending
            // callback, but discard the token so reconnect replay requests fresh auth.
            pending.token = undefined;
            return;
        }

        if (error) {
            removeTopicFromStack(topic);
        }
        settleSubscribeCallbacks(topic, error);
    };

    const subscribeToTopics = function(topics, tokens = [], timeoutMs = undefined) {
        if (activeConnection === null || topics.length === 0) {
            return false;
        }

        // Do not inspect websocket status here. During reconnect, uwebsocket tells
        // the callback to wait for setActiveConnection() to replay the topic stack.
        activeConnection.subscribe(topics, tokens, handleSubscriptionResult, timeoutMs);
        return true;
    };

    const subscribeToStackedTopics = function() {
        // Replays the whole desired subscription stack after websocket auth. Only
        // topics waiting for an ACK may still have a token; durable stack entries
        // intentionally store bare topics because tokens are connection-bound.
        const topics = topicsStack.slice();
        const tokens = topics.map(topic => subscribeCallbackStack[topic]?.token);
        subscribeToTopics(topics, tokens);
    };

    const subscribeWithCallback = function(inputTopic, callback, timeoutMs) {
        const [topic, token] = inputTopic.split('@', 2);
        if (!addTopicToStack(topic)) {
            if (subscribeCallbackStack[topic]) {
                addSubscribeCallback(topic, token, callback, timeoutMs);
                return;
            }

            // The topic is already in topicsStack and has no pending ACK, so its
            // earlier subscribe completed successfully.
            callback(null);
            return;
        }

        addSubscribeCallback(topic, token, callback, timeoutMs);
        if (!subscribeToTopics([topic], [token], timeoutMs)) {
            real.realtime.connect();
        }
    };

    const subscribeNormally = function(inputTopics) {
        if (Array.isArray(inputTopics) === false) {
            inputTopics = [inputTopics];
        }

        const topics = [];
        const tokens = [];

        // Extract possible inline tokens. They are passed to the current
        // subscribe call only; topicsStack always stores bare topic names.
        for (const idx in inputTopics) {
            const [topic, token] = inputTopics[idx].split('@', 2);
            if (addTopicToStack(topic)) {
                topics.push(topic);
                tokens.push(token);
            }
        }

        if (topics.length === 0) {
            console.log('Realtime :: No new topics to subscribe to');
            return;
        }

        if (!subscribeToTopics(topics, tokens)) {
            real.realtime.connect();
        }
    };

    const connectionIsReady = function() {
        // Connection is ready.
        isReady = true;

        // Loop through the stack of "onReady" callbacks.
        readyFnStack.forEach(function(fn) {
            fn();
        });

        // Empty the "onReady" callback stack.
        readyFnStack = [];

        // The "onConnected" stack is kept instead, because it fires on every connection rather than on the
        // first one only.
        connectedFnStack.forEach(function(fn) {
            fn();
        });
    };


    const publicMethods = {

        'setActiveConnection' : function(realtimeClass) {
            // Store active connection (either websocket or long poll)
            activeConnection = real.realtime[realtimeClass];

            // Connection is opened, subscribe to all stacked topics.
            subscribeToStackedTopics();

            // Execute "on ready" handlers
            connectionIsReady();
        },

        'connect' : function() {
            // TODO: Remove the throttling logic after uws migration.
            if (connectTimer !== null) {
                // A connection attempt is already pending.
                return;
            }

            const now = performance.now();
            const delta = connectLastAttempt ? now - connectLastAttempt : Infinity;

            if (connectAttempts >= 2 && delta < delayBetweenConnectAttempts) {
                // If a new connection attempt was tried too soon, throttle. To allow one instant
                // switch attempt, we only start throttling after the first two connects. Note that
                // the attempt counter is never reset. Also, this logic has no effect in the automatic
                // reconnects that begin after a connection is lost (those connection attempts are not
                // made with this method).
                const timeout = Math.ceil(delayBetweenConnectAttempts - delta) + 1;
                console.debug('Realtime :: Attempting connection in', timeout, 'ms.')
                connectTimer = setTimeout(
                    () => { connectTimer = null; real.realtime.connect() },
                    timeout,
                );
                return;
            }

            connectLastAttempt = now;
            connectAttempts++;

            real.realtime.uwebsocket.connect();
        },

        'subscribe' : function(inputTopics, callback = null, timeoutMs = undefined) {
            if (!inputTopics) {
                return;
            }

            const hasCallback = typeof callback === 'function';
            if (hasCallback && Array.isArray(inputTopics)) {
                callback(new Error('Realtime subscribe callback expects a single topic.'));
                return;
            }

            if (hasCallback) {
                subscribeWithCallback(inputTopics, callback, timeoutMs);
            } else {
                subscribeNormally(inputTopics);
            }
        },

        'unsubscribe' : function(topics) {
            if (activeConnection === null || !topics) {
                return;
            }

            if (Array.isArray(topics) === false) {
                topics = [topics];
            }

            const unsubscribeFromTopics = [];

            // Remove unsubscribed topics from stack
            topics.forEach(function(topic) {
                const index = topicsStack.indexOf(topic);
                if (index >= 0) {
                    unsubscribeFromTopics.push(topic);
                    removeTopicFromStack(topic);
                    settleSubscribeCallbacks(topic, new Error('Realtime topic subscription was cancelled.'));
                }
            });

            if (unsubscribeFromTopics.length === 0) {
                // No unsubscribe needed as user is not subscribed to any one of the provided topics.
                return;
            }

            activeConnection.unsubscribe(unsubscribeFromTopics);
        },

        'unsubscribeFromPageTopics' : function() {
            if (activeConnection === null) {
                return;
            }

            // Collect an array of topics to unsubscribe from in the first loop.
            const unsubscribeFromTopics = [];
            topicsStack.forEach(function(topic, index) {
                if (topic.match(/:page:/)) {
                    unsubscribeFromTopics.push(topic);
                }
            });

            if (unsubscribeFromTopics.length === 0) {
                // User is not subscribed to any page topic.
                return;
            }

            // Remove topics from stack in the second loop. If topics are removed already in the first loop
            // it will mess up with the forEach and not all topics will be looped through.
            unsubscribeFromTopics.forEach((topic) => {
                removeTopicFromStack(topic);
                settleSubscribeCallbacks(topic, new Error('Realtime topic subscription was cancelled.'));
            });

            // Do the actual unsubscribe.
            activeConnection.unsubscribe(unsubscribeFromTopics);
        },

        'onReady' : function(readyFn) {
            if (isReady) {
                readyFn();
                return;
            }

            readyFnStack.push(readyFn);
        },

        /**
         * Runs every time the connection opens, reconnects included, and stays registered.
         *
         * Nothing replays the messages published while a connection was down, so anything whose state comes
         * from those messages has to catch up from its own source each time the connection comes back.
         * "onReady" cannot do this: it fires once and clears its stack, and re-registering from inside its
         * own callback runs immediately and loops.
         */
        'onConnected' : function(connectedFn) {
            connectedFnStack.push(connectedFn);
            if (isReady) {
                connectedFn();
            }
        },

        'setIsReady' : connectionIsReady,

        'getTopics' : function() {
            return topicsStack;
        },

        'getActiveConnection' : () => {
            return activeConnection;
        },

    };

    return publicMethods;

})(real);
